import React from 'react';
import { FormattedMessage } from 'react-intl';
import './printedContentV2.scss';
import { formatDate } from '../../../utils/variableEvaluation';
import { toFixedNumber } from '../../../utils/react';
import { ModalContent } from '../../../types/modalContent.type';

interface Props {
    offer: ModalContent;
    message?: string;
    attachments?: any[];
    companyProfile?: any;
    logoBase64?: string | null;
}

export class PrintedContentV2 extends React.PureComponent<Props> {
    render() {
        const { offer, message, attachments, companyProfile, logoBase64 } = this.props;

        // Dynamic colors from company profile, with fallbacks
        const primaryColor = companyProfile?.pdfPrimaryColor || '#1e3a5f';
        const secondaryColor = companyProfile?.pdfSecondaryColor || '#dce8f5';

        // Company info from profile, with fallbacks
        const companyName = companyProfile?.name || '';
        const companyPhone = companyProfile?.phone || '';
        const companyEmail = companyProfile?.email || '';
        const companyAddress = [companyProfile?.address, companyProfile?.city].filter(Boolean).join(', ') || '';
        const companyFiscalNumber = companyProfile?.fiscalNumber || '';

        // Format offer ID using the company's offer ID format
        const offerIdFormat = companyProfile?.offerIdFormat || '{id}';
        const rawOfferId = offer?.invoiceNumber || '';
        const now = new Date();
        const formattedOfferId = offerIdFormat
            .replace('{dd-MM-yyyy}', `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`)
            .replace('{MM-yyyy}', `${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`)
            .replace('{id}', String(rawOfferId))
            .replace('{yyyy}', String(now.getFullYear()))
            .replace('{MM}', String(now.getMonth() + 1).padStart(2, '0'))
            .replace('{dd}', String(now.getDate()).padStart(2, '0'));

        const products = offer?.products || [];

        const services = (offer as any)?.services || [];

        let grandTotal = products.reduce((sum: number, item: any) => {
            if (item?.canceled !== 1) {
                sum += Number(item?.totalPrice || 0);
                if (item?.subProducts?.length) {
                    item.subProducts.forEach((sp: any) => {
                        sum += Number(sp?.totalPrice || 0);
                    });
                }
            }
            return sum;
        }, 0);

        // Service prices always count toward the total; the currency is just a
        // display label on the line.
        services.forEach((svc: any) => {
            if (svc.enabled) grandTotal += Number(svc.price || 0);
        });

        const hasDiscount = offer?.discountValue && Number(offer.discountValue) > 0;
        const discountAmount = hasDiscount
            ? (offer.discountType === 1
                ? grandTotal * Number(offer.discountValue) / 100
                : Number(offer.discountValue))
            : 0;
        const finalTotal = hasDiscount
            ? (offer?.discountedTotal != null ? Number(offer.discountedTotal) : grandTotal - discountAmount)
            : grandTotal;

        const cityCountry = [offer?.city, offer?.country].filter(Boolean).join(', ');

        return (
            <div id="printContainer" style={{ '--pdf-primary': primaryColor, '--pdf-secondary': secondaryColor } as React.CSSProperties}>

                {/* Header: Logo — Ofertë — ID/Date */}
                <div className="print-header-row" style={{ background: primaryColor }}>
                    <div className="print-header-left">
                        {(logoBase64 || companyProfile?.logo) && (
                            <img src={logoBase64 || companyProfile.logo} alt="" className="print-logo-img" />
                        )}
                    </div>
                    <div className="print-header-center">
                        <span className="print-offer-label"><FormattedMessage id="offer.pdf.title" defaultMessage="Offer" /></span>
                    </div>
                    <div className="print-header-right">
                        {formattedOfferId && <span className="print-header-id">{formattedOfferId}</span>}
                        {offer?.date && <span className="print-header-date">{formatDate(offer.date)}</span>}
                    </div>
                </div>

                {/* Shitësi / Blerësi */}
                <div className="print-info-row" style={{ borderTopColor: primaryColor }}>
                    <div className="print-info-block">
                        <p className="print-info-block-title" style={{ color: primaryColor }}><FormattedMessage id="offer.pdf.seller" defaultMessage="Seller" /></p>
                        {companyName && <div className="print-info-line">
                            <span className="print-info-label"><FormattedMessage id="offer.pdf.label.name" defaultMessage="Name:" /></span>
                            <span className="print-info-value">{companyName}</span>
                        </div>}
                        {companyPhone && <div className="print-info-line">
                            <span className="print-info-label"><FormattedMessage id="offer.pdf.label.phone" defaultMessage="Phone:" /></span>
                            <span className="print-info-value">{companyPhone}</span>
                        </div>}
                        {companyEmail && <div className="print-info-line">
                            <span className="print-info-label"><FormattedMessage id="offer.pdf.label.email" defaultMessage="Email:" /></span>
                            <span className="print-info-value">{companyEmail}</span>
                        </div>}
                        {companyAddress && <div className="print-info-line">
                            <span className="print-info-label"><FormattedMessage id="offer.pdf.label.address" defaultMessage="Address:" /></span>
                            <span className="print-info-value">{companyAddress}</span>
                        </div>}
                        {companyFiscalNumber && <div className="print-info-line">
                            <span className="print-info-label"><FormattedMessage id="offer.pdf.label.fiscalNumber" defaultMessage="Fiscal no.:" /></span>
                            <span className="print-info-value">{companyFiscalNumber}</span>
                        </div>}
                    </div>

                    <div className="print-info-block">
                        <p className="print-info-block-title" style={{ color: primaryColor }}><FormattedMessage id="offer.pdf.buyer" defaultMessage="Buyer" /></p>
                        <div className="print-info-line">
                            <span className="print-info-label"><FormattedMessage id="offer.pdf.label.name" defaultMessage="Name:" /></span>
                            <span className="print-info-value">{offer?.firstAndLastName}</span>
                        </div>
                        {offer?.project && (
                            <div className="print-info-line">
                                <span className="print-info-label"><FormattedMessage id="offer.pdf.label.project" defaultMessage="Project:" /></span>
                                <span className="print-info-value">{offer.project}</span>
                            </div>
                        )}
                        {offer?.phoneNumber && (
                            <div className="print-info-line">
                                <span className="print-info-label"><FormattedMessage id="offer.pdf.label.phone" defaultMessage="Phone:" /></span>
                                <span className="print-info-value">{offer.phoneNumber}</span>
                            </div>
                        )}
                        <div className="print-info-line">
                            <span className="print-info-label"><FormattedMessage id="offer.pdf.label.address" defaultMessage="Address:" /></span>
                            <span className="print-info-value">{offer?.address}</span>
                        </div>
                        {cityCountry && (
                            <div className="print-info-line">
                                <span className="print-info-label"><FormattedMessage id="offer.pdf.label.city" defaultMessage="City:" /></span>
                                <span className="print-info-value">{cityCountry}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Products — each product is its own block with its own header */}
                <div className="print-products-container">
                    <div className="print-section-label" style={{ color: primaryColor }}><FormattedMessage id="offer.pdf.products" defaultMessage="Products" /></div>
                    {products.map((item: any, index: number) => (
                        <div className="print-product-section" key={index}>
                            <table className="print-product-table">
                                <thead>
                                    <tr>
                                        <th className="col-pos"><FormattedMessage id="offer.subProduct.col.pos" defaultMessage="Pos." /></th>
                                        <th className="col-article"><FormattedMessage id="offer.product.col.article" defaultMessage="Article" /></th>
                                        <th className="col-desc"><FormattedMessage id="offer.pdf.col.description" defaultMessage="Description" /></th>
                                        <th className="col-qty"><FormattedMessage id="offer.product.col.quantity" defaultMessage="Quantity" /></th>
                                        <th className="col-price"><FormattedMessage id="offer.product.col.price" defaultMessage="Price" /></th>
                                        <th className="col-total"><FormattedMessage id="offer.product.col.total" defaultMessage="Total" /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Main data row */}
                                    <tr className="data-row">
                                        <td className="col-pos">{index + 1}</td>
                                        <td className="col-article">
                                            {item?.type && (
                                                <div className="article-code-above-image">{item.type}</div>
                                            )}
                                            {item?.articleImage && (
                                                <div className="article-image-wrapper">
                                                    <img
                                                        src={item.articleImage}
                                                        alt={item?.designation?.label}
                                                        crossOrigin="anonymous"
                                                    />
                                                </div>
                                            )}
                                        </td>
                                        <td className="col-desc">
                                            <div className="article-field">
                                                <span className="article-field-value article-name">
                                                    {item?.designation?.label}
                                                </span>
                                            </div>
                                            {item?.articleDescription && (
                                                <div className="article-field">
                                                    <span className="article-field-value article-desc">
                                                        {item.articleDescription}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="col-qty">
                                            {item?.unit}{item?.unitName ? ` ${item.unitName}` : ''}
                                        </td>
                                        <td className="col-price">
                                            {toFixedNumber(Number(item?.price || 0), 2)} €
                                        </td>
                                        <td className="col-total">
                                            {toFixedNumber(Number(item?.totalPrice || 0), 2)} €
                                        </td>
                                    </tr>

                                    {/* Sub-product rows */}
                                    {item?.subProducts?.length > 0 && item.subProducts.map((sp: any, spIdx: number) => (
                                        <tr className="sub-product-pdf-row" key={`sp-${spIdx}`}>
                                            <td className="col-pos sub-pos">{index + 1}.{spIdx + 1}</td>
                                            <td className="col-article"></td>
                                            <td className="col-desc">
                                                <div className="article-field">
                                                    <span className="article-field-value sub-product-name">
                                                        {sp?.name}
                                                    </span>
                                                </div>
                                                {sp?.description && (
                                                    <div className="article-field">
                                                        <span className="article-field-value article-desc">
                                                            {sp.description}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="col-qty">{sp?.quantity}</td>
                                            <td className="col-price">
                                                {toFixedNumber(Number(sp?.effectivePrice || 0) * Number(sp?.coefficient || 1), 2)} €
                                            </td>
                                            <td className="col-total">
                                                {toFixedNumber(Number(sp?.totalPrice || 0), 2)} €
                                            </td>
                                        </tr>
                                    ))}

                                    {/* Note row — spans all columns, always shown */}
                                    <tr className="note-row">
                                        <td colSpan={6} className="col-note">
                                            <span className="print-note-label"><FormattedMessage id="offer.product.note" defaultMessage="Note:" /> </span>
                                            <span className="print-note-text">{item.note || ''}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>

                {/* Services */}
                {services.length > 0 && (
                    <div className="print-services-section print-product-section">
                        <div className="print-section-label" style={{ color: primaryColor }}><FormattedMessage id="offer.modal.services.title" defaultMessage="Services" /></div>
                        <table className="print-services-table">
                            <thead>
                                <tr>
                                    <th className="print-svc-col-name"><FormattedMessage id="offer.pdf.col.service" defaultMessage="Service" /></th>
                                    <th className="print-svc-col-desc"><FormattedMessage id="offer.pdf.col.description" defaultMessage="Description" /></th>
                                    <th className="print-svc-col-price"><FormattedMessage id="offer.modal.services.pricePlaceholder" defaultMessage="Value" /></th>
                                </tr>
                            </thead>
                            <tbody>
                                {services.map((svc: any, i: number) => {
                                    const serviceKey = svc.serviceType === 'transport'
                                        ? 'offer.modal.services.transport'
                                        : 'offer.modal.services.installation';
                                    const serviceDefault = svc.serviceType === 'transport' ? 'Transport' : 'Installation';
                                    const wrapperKey = svc.enabled ? 'offer.modal.services.with' : 'offer.modal.services.without';
                                    const wrapperDefault = svc.enabled ? 'With {service}' : 'Without {service}';
                                    return (
                                        <tr key={i}>
                                            <td className="print-svc-col-name">
                                                <FormattedMessage
                                                    id={wrapperKey}
                                                    defaultMessage={wrapperDefault}
                                                    values={{ service: <FormattedMessage id={serviceKey} defaultMessage={serviceDefault} /> }}
                                                />
                                            </td>
                                            <td className="print-svc-col-desc">{svc.text || ''}</td>
                                            <td className="print-svc-col-price">
                                                {svc.enabled ? `${toFixedNumber(Number(svc.price || 0), 2)} ${svc.currency || 'EUR'}` : '\u2014'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Totals */}
                <div className="print-totals-block">
                    <div className="print-total-line positions">
                        <span className="print-total-line-label"><FormattedMessage id="offer.pdf.totalPositions" defaultMessage="Total positions:" /></span>
                        <span className="print-total-line-value">
                            <FormattedMessage
                                id="offer.pdf.positions"
                                defaultMessage="{count, plural, one {# position} other {# positions}}"
                                values={{ count: products.filter((p: any) => p?.canceled !== 1).length }}
                            />
                        </span>
                    </div>
                    <div className="print-total-line subtotal">
                        <span className="print-total-line-label"><FormattedMessage id="offer.pdf.subtotal" defaultMessage="Subtotal:" /></span>
                        <span className="print-total-line-value">{toFixedNumber(grandTotal, 2)} €</span>
                    </div>
                    {hasDiscount && (
                        <div className="print-total-line discount-line">
                            <span className="print-total-line-label">
                                <FormattedMessage id="offer.pdf.discount" defaultMessage="Discount" />{offer.discountType === 1 ? ` (${offer.discountValue}%)` : ''}:
                            </span>
                            <span className="print-total-line-value has-discount">
                                -{toFixedNumber(discountAmount, 2)} €
                            </span>
                        </div>
                    )}
                    <div className="print-total-line final">
                        <span className="print-total-line-label"><FormattedMessage id="offer.pdf.total" defaultMessage="Total:" /></span>
                        <span className="print-total-line-value">{toFixedNumber(finalTotal, 2)} €</span>
                    </div>
                </div>

                {offer?.comment && (
                    <div className="print-comment">
                        <span className="print-comment-label"><FormattedMessage id="offer.pdf.notice" defaultMessage="Notice:" /></span>
                        <span className="print-comment-text">{offer.comment}</span>
                    </div>
                )}


                {message && message.replace(/<[^>]*>/g, '').trim().length > 0 && (
                    <div className="print-conditions print-product-section" style={{ borderLeftColor: primaryColor }}>
                        <p className="print-conditions-title" style={{ color: primaryColor }}><FormattedMessage id="offer.form.conditionsTitle" defaultMessage="Offer terms" /></p>
                        <div className="print-conditions-content" dangerouslySetInnerHTML={{ __html: message }}/>
                    </div>
                )}
            </div>
        );
    }
}
